import { useEffect, useState } from "react"
import {
    AppBar,
    Button,
    Box,
    Card,
    CardContent,
    Dialog,
    IconButton,
    Stack,
    Toolbar,
    Typography,
    MenuItem,
    ListItemIcon,
    ListItemText
} from "@mui/material"
import {
    Datagrid,
    DateField,
    FunctionField,
    Labeled,
    List,
    SelectInput,
    TextField,
    TextInput,
    useListContext,
    useNotify,
} from "react-admin"
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import RocketTwoToneIcon from '@mui/icons-material/RocketTwoTone';
import HouseTwoToneIcon from '@mui/icons-material/HouseTwoTone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { provinces } from "../../utils";
import restProvider from "../../../../rest.provider";

import { OrdersPagination } from "../../components/Pagination";
import { VBagLabel, VStudentLabel, zBagLabel, zStudentLabel } from "./components";

export const StudentOrderList = () => {
    const [labels, setLabels] = useState(false)
    const [printers, setPrinters] = useState({
        box: undefined,
        bag: undefined,
        student: undefined
    })
    const [studentLabels, setStudentLabels] = useState(() => {
        const settings = JSON.parse(localStorage.getItem('zWarehouse')) || {}
        return settings.studentLabels || 40
    })
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const notify = useNotify()

    // Function to get printer selections from localStorage (same as Settings and Custom Labels)
    const getPrinterSelections = () => {
        try {
            const stored = localStorage.getItem('zWarehouse_printer_selections');
            const selections = stored ? JSON.parse(stored) : {};
            const defaultSelections = { box: null, bag: null, student: null };
            return { ...defaultSelections, ...selections };
        } catch (error) {
            console.error('Error loading printer selections:', error);
            return { box: null, bag: null, student: null };
        }
    };

    const Filters = [
        <TextInput source='batch' />,
        <TextInput label='City' source='customer.address.city' />,
        <TextInput label='School' source='school' />,
        <TextInput label='Grade/Kit' source='grade' />,
        <SelectInput
            label='Province'
            source='customer.address.province'
            optionText='province'
            optionValue='province'
            choices={provinces} />
    ]

    const sort = { field: 'batch', order: 'DESC'}

    const handleMenuOpen = (event) => {
        console.log('Menu opened, target:', event.currentTarget)
        setMenuAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
    }

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both the button and the dropdown
            const buttonElement = menuAnchorEl
            const dropdownElement = document.querySelector('[data-dropdown="print-labels"]')
            
            if (buttonElement && 
                !buttonElement.contains(event.target) && 
                !dropdownElement?.contains(event.target)) {
                handleMenuClose()
            }
        }

        if (Boolean(menuAnchorEl)) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [menuAnchorEl])

    const handlePreview = (labelType) => {
        setLabels(labelType)
        handleMenuClose()
    }

    const handleDirectPrint = async (labelType, contextData) => {
        handleMenuClose()
        
        // Use passed context data
        const { data, selectedIds } = contextData
        
        // Debug logging to understand the context data
        console.log('Direct Print Debug:', { 
            dataLength: data?.length, 
            selectedIdsLength: selectedIds?.length, 
            selectedIds, 
            data: data?.slice(0, 2) // First 2 items for debugging
        })
        
        // Get selected orders from context data
        const orders = data ? selectedIds.map(id => data.find(item => item.id === id)).filter(Boolean) : []
        
        console.log('Orders found:', orders.length, orders.slice(0, 2))
        
        if (orders.length === 0) {
            notify('No orders selected', { type: 'warning' })
            return
        }

        // Execute print logic directly
        let printData = ''

        switch (labelType) {
            case 'bag':
                orders.forEach(order => {
                    printData = printData + zBagLabel(order)
                })

                try {
                    printers.bag.send(printData)
                    notify('Bag labels printed successfully', { type: 'success' })
                } catch (e) {
                    console.error("Oops!", e)
                    notify('Unable to print!  Printer not responding', { type: 'error' })
                }
                break

            case 'student':
                // Get current printer selections from localStorage
                const printerSelections = getPrinterSelections();
                const studentPrinter = printerSelections.student;
                
                // Use API printer if available, otherwise fall back to legacy printer
                if (studentPrinter && studentPrinter.ip) {
                    try {
                        // Print each order's student labels using the student_labels format
                        for (const order of orders) {
                            const studentName = order.student || 'Student'
                            
                            // Send to printer API using student_labels format
                            const printResult = await restProvider.printLabel('printer', {
                                labelType: 'student_labels',
                                labelData: {
                                    studentName: studentName,
                                    numLabels: +studentLabels,
                                    defaultFontSize: 56,
                                    abbreviateLongNames: true
                                },
                                printer: studentPrinter.ip
                            })

                            if (!printResult.data || !printResult.data.success) {
                                const errorMessage = printResult.data?.message || 'Failed to print student labels via API'
                                notify(errorMessage, { type: 'error' })
                                break // Stop printing if one fails
                            }
                        }

                        const printerName = studentPrinter.name || studentPrinter.ip
                        const printerLocation = studentPrinter.location ? ` (${studentPrinter.location})` : ''
                        notify(`Student labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                    } catch (error) {
                        console.error('API print error:', error)
                        notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                        
                        // Fall back to legacy printer
                        try {
                            let legacyData = ''
                            orders.forEach(order => {
                                for (let i = 0; i < +studentLabels; i++) {
                                    legacyData += '^XA' + zStudentLabel(order) + '^XZ'
                                }
                            })
                            printers.student.send(legacyData)
                            notify('Student labels printed via legacy printer', { type: 'success' })
                        } catch (legacyError) {
                            console.error("Legacy printer error:", legacyError)
                            notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                        }
                    }
                } else {
                    // Use legacy printer
                    try {
                        orders.forEach(order => {
                            for (let i = 0; i < +studentLabels; i++) {
                                printData += '^XA' + zStudentLabel(order) + '^XZ'
                            }
                        })
                        printers?.student.send(printData)
                        notify('Student labels printed via legacy printer', { type: 'success' })
                    } catch(e) {
                        console.error("Legacy printer error:", e)
                        notify('Unable to print!  Printer not responding', { type: 'error'})
                    }
                }
                break

            default:
                break
        }
    }

    const Actions = () => {
        // Get context data inside the Actions component
        const { data, selectedIds } = useListContext()
        
        return (
            <>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Button
                        variant='outlined'
                        onClick={handleMenuOpen}
                        endIcon={<MoreVertIcon />}
                    >
                        Print Labels
                    </Button>
                    {Boolean(menuAnchorEl) && (
                    <Box
                        data-dropdown="print-labels"
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            zIndex: 1300,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            boxShadow: 3,
                            minWidth: 200,
                            display: 'block',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* <MenuItem onClick={() => handlePreview('bag')}>
                            <ListItemIcon>
                                <VisibilityIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Preview Bag Labels</ListItemText>
                        </MenuItem> */}
                        <MenuItem onClick={() => handleDirectPrint('bag', { data, selectedIds })}>
                            <ListItemIcon>
                                <PrintIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Print Bag Labels</ListItemText>
                        </MenuItem>
                        {/* <MenuItem onClick={() => handlePreview('student')}>
                            <ListItemIcon>
                                <VisibilityIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Preview Student Labels</ListItemText>
                        </MenuItem> */}
                        <MenuItem onClick={() => handleDirectPrint('student', { data, selectedIds })}>
                            <ListItemIcon>
                                <PrintIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Print Student Labels</ListItemText>
                        </MenuItem>
                    </Box>
                )}
                </Box>
            </>
        )
    }

    const Preview = () => {
        const { data, selectedIds } = useListContext()
        const [orders, setOrders] = useState([])

        const handlePrint = async () => {
            let data = ''

            switch (labels) {
                case 'bag':
                    orders.forEach(order => {
                        data = data + zBagLabel(order)
                    })

                    try {
                        printers.bag.send(data)
                        notify('Bag labels printed successfully', { type: 'success' })
                        setLabels(false) // Close the modal after successful print
                    } catch (e) {
                        console.error("Oops!", e)
                        notify('Unable to print!  Printer not responding', { type: 'error' })
                    }
                    
                    break

                case 'student':

                    // Get current printer selections from localStorage
                    const printerSelections = getPrinterSelections();
                    const studentPrinter = printerSelections.student;

                    // Use API printer if available, otherwise fall back to legacy printer
                    if (studentPrinter && studentPrinter.ip) {
                        try {
                            // Print each order's student labels using the student_labels format
                            for (const order of orders) {
                                const studentName = order.student || 'Student'
                                
                                // Send to printer API using student_labels format
                                const printResult = await restProvider.printLabel('printer', {
                                    labelType: 'student_labels',
                                    labelData: {
                                        studentName: studentName,
                                        numLabels: +studentLabels,
                                        defaultFontSize: 56,
                                        abbreviateLongNames: true
                                    },
                                    printer: studentPrinter.ip
                                })

                                if (!printResult.data || !printResult.data.success) {
                                    const errorMessage = printResult.data?.message || 'Failed to print student labels via API'
                                    notify(errorMessage, { type: 'error' })
                                    break // Stop printing if one fails
                                }
                            }

                            const printerName = studentPrinter.name || studentPrinter.ip
                            const printerLocation = studentPrinter.location ? ` (${studentPrinter.location})` : ''
                            notify(`Student labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                            setLabels(false) // Close the modal after successful print
                        } catch (error) {
                            console.error('API print error:', error)
                            notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                            
                            // Fall back to legacy printer
                            try {
                                let legacyData = ''
                                orders.forEach(order => {
                                    for (let i = 0; i < +studentLabels; i++) {
                                        legacyData += '^XA' + zStudentLabel(order) + '^XZ'
                                    }
                                })
                                printers.student.send(legacyData)
                                notify('Student labels printed via legacy printer', { type: 'success' })
                                setLabels(false) // Close the modal after successful print
                            } catch (legacyError) {
                                console.error("Legacy printer error:", legacyError)
                                notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                                setLabels(false) // Close the modal even on error
                            }
                        }
                    } else {
                        // Use legacy printer
                        try {
                            orders.forEach(order => {
                                for (let i = 0; i < +studentLabels; i++) {
                                    data += '^XA' + zStudentLabel(order) + '^XZ'
                                }
                            })
                            printers?.student.send(data)
                            notify('Student labels printed via legacy printer', { type: 'success' })
                            setLabels(false) // Close the modal after successful print
                        } catch(e) {
                            console.error("Legacy printer error:", e)
                            notify('Unable to print!  Printer not responding', { type: 'error'})
                            setLabels(false) // Close the modal even on error
                        }
                    }

                    break

                default:
                    break
            }
        }
 
        useEffect(() => {
            let selected = []
            if (data) {
                selectedIds.forEach(id => {
                    selected = [...selected, data.find(item => item.id === id)]
                })
                setOrders(selected)
            }

        }, [data, selectedIds])

        return (
            <Dialog open={labels ? true : false} onClose={()=>setLabels()} fullWidth>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar sx={{ justifyContent: 'space-between'}}>
                        <Typography sx={{ ml: 2, flex: 1 }} variant='h5' component='div'>
                            {labels === 'bag' && 'Bag Labels (preview)'}
                            {labels === 'student' && `Student Labels (x${studentLabels})`}
                        </Typography>

                        <Stack direction='row' spacing={1}>
                            <IconButton
                                color='inherit'
                                onClick={handlePrint}>
                                <PrintIcon fontSize='large' />
                            </IconButton>
                            
                            <IconButton
                                color='inherit'
                                onClick={()=>setLabels()}
                                aria-label="close">
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                    </Toolbar>
                </AppBar>

                <Box>
                    {labels === 'bag' && orders && orders.map((order, idx) =>
                        <VBagLabel order={order} key={idx} />
                    )}
                    {labels === 'student' && orders && orders.map((order, idx) =>
                        <VStudentLabel order={order} key={idx} />
                    )}
                </Box>
            </Dialog>
        )
    }

    useEffect(() => {
        const settings = JSON.parse(localStorage.getItem('zWarehouse'))
        const { printers, studentLabels } = settings
        const { printer } = window.devices

        if (printer) {
            setPrinters({
                box: printer.find(p => p.uid === printers['box']?.uid),
                bag: printer.find(p => p.uid === printers['bag']?.uid),
                student: printer.find(p => p.uid === printers['student']?.uid)
            })
        }

        if (studentLabels) {
            setStudentLabels(studentLabels)
        }
    }, [])

    return (
        <Card>
            <CardContent>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <HouseTwoToneIcon sx={{ fontSize: 46 }} color="primary" />
                    <Typography variant='h4' color='primary'>
                        Home Delivery
                    </Typography>
                    <Typography variant='h5' color='primary'>Student Orders 'On Deck'</Typography>
                    <RocketTwoToneIcon color='secondary' />
                </Stack>
                <List
                    filters={Filters} sort={sort}
                    pagination={<OrdersPagination />}>
                    <>
                        <Preview />
                        <Datagrid bulkActionButtons={<Actions />}>
                            <TextField source='batch' />

                            <FunctionField label='ID' render={record =>
                                `K-${record.orderId}-${record.id}`} />

                            <Labeled label='Order Date'>
                                <DateField source='order.date' label={false} />
                            </Labeled>

                            <TextField source='school' />
                            <TextField source='customer.address.city' label='City' />
                            <TextField source='customer.address.province' label='Province' />
                            <TextField source='student' />
                            <TextField source='kit.name' label='Grade/Kit' />
                        </Datagrid>
                    </>
                </List>
            </CardContent>
        </Card>
    )
}